import * as React from 'react';
import { Autocomplete, TextField } from '@mui/material';

export interface IDAutocompleteProps<T> {
    itemText: keyof T;
    itemValue: keyof T;
    label: string;
    helperText?: string;
    onChange?: (val: T | null) => void;
    value: T | null;
    options: T[];
    disabled?: boolean;
    disableClearable?: boolean;
    disableGutters?: boolean;
    // Minimum width of the input. Defaults to 25rem to preserve the historical
    // sizing; pass a smaller value (e.g. 0) when the autocomplete lives in a
    // narrow flex/grid cell so it shrinks with its container instead of
    // overflowing and overlapping its neighbors.
    minWidth?: string | number;
    // Optional section header per option, passed straight to MUI's Autocomplete.
    // MUI does NOT sort by group — it only inserts a header whenever the group
    // changes — so `options` must already be ordered with each group contiguous,
    // or the same header renders more than once.
    groupBy?: (option: T) => string;
}

export function DAutocomplete<T>({
    itemValue,
    itemText,
    value,
    options,
    onChange,
    disabled = false,
    helperText,
    disableClearable = false,
    label,
    disableGutters = true,
    minWidth = '25rem',
    groupBy,
    ...rest
}: IDAutocompleteProps<T>) {
    return (
        <Autocomplete
            {...rest}
            groupBy={groupBy}
            disabled={disabled}
            isOptionEqualToValue={(option, value: T) => {
                return option[itemValue] === value[itemValue];
            }}
            value={value}
            onChange={(event, val) => {
                if (onChange && !Array.isArray(val)) {
                    onChange(val);
                }
            }}
            getOptionLabel={(val) => {
                if (!val) return '';
                if (typeof val === 'string' || typeof val === 'number')
                    return String(val);
                return String(val[itemText]);
            }}
            options={options}
            disableClearable={disableClearable}
            renderInput={(params) => {
                return (
                    <TextField
                        {...params}
                        disabled={disabled}
                        sx={{
                            my: !disableGutters ? 2 : 0,
                            width: '100%',
                            minWidth,
                        }}
                        label={label}
                        helperText={helperText || ''}
                        margin={'dense'}
                        size={'small'}
                    />
                );
            }}
        />
    );
}
